import { Form } from "components/Form/Form";
import FormInput from "components/FormInput/FormInput";
import FormMultiFileInput from "components/FormMultiFileInput/FormMultiFileInput";
import FormSearchableSelect from "components/FormSearchableSelect/FormSearchableSelect";
import { exactStringMatchFilter } from "entities/CostLines/utils/exactStringMatchFilter";
import { CurrencyExchange } from "features/CurrencyExchange";
import { Fragment } from "react";
import { Col, Container, Popover, Row, Stack } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { ROLES } from "shared/config";
import { Check, DatePicker } from "shared/ui";
import NumericInput from "shared/ui/NumericInput/NumericInput";
import Select from "shared/ui/Select/Select";
import { JobRelatedExpense } from "features/CostLine";

function ExpensesRequestCostLineView({
  idx,
  costLineData,
  form,
  isJobRelatedAllowed,
  handleSubmit,
  vatCodesOptions,
  disabled = false,
  validations = null,
  isMileageCost,
  vehicleOptions,
  viewRequestAs,
  errors,
  mileageUnit,
  displayExpenseDoctypes,
  expenseDoctypes,
  isExpenseDoctypeCheckDisabled,
  currenciesOptions,
  isCurrencySelectDisabled,
  isCompanyCardPaymentAllowed,
  isCostTypeSelectDisabled,
  isVatSelectDisabled,
  isJobRelated,
  costTypeOptions,
  displayCurrencyExchange = false,
}) {
  const { t } = useTranslation("CostLine");
  return (
    <Form form={form} onSubmit={handleSubmit}>
      <Container className="text-start" fluid>
        {isJobRelatedAllowed ? (
          <JobRelatedExpense
            // @ts-ignore
            disabled={disabled}
            costTypeOptions={costTypeOptions}
            costLineData={costLineData}
          />
        ) : null}
        {isCompanyCardPaymentAllowed && (
          <Row className="mb-4">
            <Check
              id={`company-card-payment-checkbox_costline-${idx}`}
              register={form.register}
              name="company_card_payment"
              type="switch"
              label={t("labels.company_card_payment")}
              disabled={disabled}
            />
          </Row>
        )}
        <>
          <Row>
            <Col md={6}>
              <Col>
                <FormSearchableSelect
                  inputId={`cost-type-select-${idx}`}
                  name="cost_type"
                  isDisabled={isCostTypeSelectDisabled}
                  options={costTypeOptions}
                  label={t("labels.cost_type")}
                />
              </Col>
              <Row className="mt-4">
                <Col>
                  {isMileageCost ? (
                    <NumericInput
                      label={t("labels.distance")}
                      name="distance"
                      addon={mileageUnit}
                      disabled={disabled}
                      displayAs={Col}
                      allowNegative={false}
                      allowLeadingZeros={false}
                      thousandSeparator=","
                      decimalScale={1}
                      error={errors?.distance}
                      md={12}
                    />
                  ) : (
                    <Stack gap={4}>
                      <Select
                        label={t("labels.currency")}
                        name="currency_code"
                        isDisabled={isCurrencySelectDisabled}
                        displayAs={Col}
                        isSearchable={true}
                        shouldUnregister={false}
                        options={currenciesOptions}
                        error={errors?.currency_code}
                        filterOption={exactStringMatchFilter}
                        md={12}
                      />
                      <NumericInput.WithPopover
                        valueIsNumericString={false}
                        popover={
                          !disabled ? (
                            <Popover body>
                              <Trans
                                as={Popover}
                                i18nKey="cost_line_amount"
                                ns="CostLine"
                              >
                                Please enter in the format 12 345,67 <br />
                                min 0,01 max 999 999 999 999,99 <br />
                                Only 1 decimal separator allowed, comma or
                                point. Only spaces allowed as thousand’s
                                separators.
                              </Trans>
                            </Popover>
                          ) : (
                            <Fragment />
                          )
                        }
                        name="amount"
                        label={t("labels.amount")}
                        displayAs={Col}
                        md={12}
                        decimalScale={2}
                        allowNegative={false}
                        thousandSeparator={" "}
                        error={errors?.amount}
                        disabled={
                          disabled ||
                          (costLineData?.acceptations?.data?.rejection_reasons?.data?.filter(
                            ({ rejection_name }) =>
                              rejection_name !== "Wrong cost type" &&
                              rejection_name !== "Wrong VAT Code" &&
                              rejection_name !== "Wrong cost date"
                          ).length === 0 &&
                            costLineData?.acceptations?.data?.rejection_reasons
                              ?.data?.length > 0)
                        }
                      />
                    </Stack>
                  )}
                </Col>
                <Col>
                  {isMileageCost ? (
                    <Select
                      label={t("labels.car_type")}
                      name="vehicle_id"
                      isDisabled={disabled}
                      displayAs={Col}
                      isSearchable={false}
                      options={vehicleOptions}
                      error={errors?.vehicle_id}
                      md={12}
                    />
                  ) : (
                    <Stack gap={4}>
                      <Select
                        label={t("labels.vat")}
                        name="vat"
                        isDisabled={isVatSelectDisabled}
                        displayAs={Col}
                        isSearchable={false}
                        isClearable={isJobRelated}
                        options={vatCodesOptions}
                        error={errors?.vat}
                        md={12}
                      />
                      <DatePicker
                        label={t("labels.date")}
                        name="purchase_date"
                        actions={["cancel", "accept"]}
                        disabled={disabled}
                        disableFuture
                        error={errors?.purchase_date}
                      />
                    </Stack>
                  )}
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <FormInput
                name="description"
                as="textarea"
                label={t("labels.cost_description")}
                displayAs={Col}
                text={t("cost_description_text")}
                required
                className="mb-3"
                disabled={disabled}
              />
              <FormMultiFileInput
                name="attachments"
                attachedFiles={costLineData?.attachments.data}
                acceptedExtensions={validations?.attachments.attachments_allowed_file_types?.data
                  .map((ext) => `.${ext}`)
                  .join(", ")}
                toDisplay={
                  viewRequestAs.role === ROLES.ACCOUNTS_PAYABLE || disabled
                }
                maxNumberOfFiles={
                  validations?.attachments.attachments_count_rules?.data.max
                }
                maxAllowedFileSize={
                  validations?.attachments.max_allowed_file_size_in_bytes
                }
              />
            </Col>
            {displayCurrencyExchange && (
              <CurrencyExchange
                costLineId={costLineData.id}
                storedExchange={costLineData.converted_currency}
              />
            )}
            {displayExpenseDoctypes && expenseDoctypes?.length && (
              <>
                <div className="border-top mt-5 mb-4" />
                <Col md>
                  <label>{t("labels.expense_document_type")}</label>
                  <Stack gap={2} className="p-3 pb-0">
                    {expenseDoctypes?.map(({ id, name }) => (
                      <Check
                        key={id}
                        id={`expense-doctype-checkbox-${id}__costline-${idx}`}
                        register={form.register}
                        name="expense_document_type"
                        type="radio"
                        value={name}
                        label={name}
                        disabled={isExpenseDoctypeCheckDisabled}
                      />
                    ))}
                  </Stack>
                </Col>
              </>
            )}
          </Row>
        </>
      </Container>
    </Form>
  );
}

export default ExpensesRequestCostLineView;
